(() => {
  function initTapnHeader(root = document) {
    const header = root.matches?.('[data-tapn-header]') ? root : root.querySelector?.('[data-tapn-header]');
    if (!header || header.dataset.tapnHeaderBound === 'true') return;
    header.dataset.tapnHeaderBound = 'true';

    const lifecycle = new AbortController();
    const listenerOptions = { signal: lifecycle.signal };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileCartViewport = window.matchMedia('(max-width: 63.999rem)');
    const mobileMenu = header.querySelector('.tapn-header__mobile-menu');
    const mobileMenuSummary = mobileMenu?.querySelector('summary');
    const mobileSubmenus = mobileMenu ? [...mobileMenu.querySelectorAll('[data-tapn-mobile-submenu]')] : [];
    // Généralisé le 12 septembre 2026 (ajout du menu « Services », demande
    // d'Amine) : jusque-là figé sur le seul menu Cartes (`cardsMenuItem`/
    // `cardsMenuButton`), pilote maintenant chaque `.tapn-header__nav-item
    // --dropdown` de la même façon (survol, clic, clavier), sans dupliquer
    // la logique par menu.
    const dropdownMenus = [...header.querySelectorAll('.tapn-header__nav-item--dropdown')].map((item) => ({
      item,
      button: item.querySelector('.tapn-header__link'),
    }));
    const brand = header.querySelector('.tapn-header__brand');
    const searchButton = header.querySelector('[data-tapn-search-open]');
    const searchOverlay = document.querySelector('[data-tapn-search-overlay]');
    const searchDialog = searchOverlay?.querySelector('[data-tapn-search-dialog]');
    const searchForm = searchOverlay?.querySelector('[data-tapn-search-form]');
    const searchInput = searchOverlay?.querySelector('[data-tapn-search-input]');
    const searchClear = searchOverlay?.querySelector('[data-tapn-search-clear]');
    const searchSuggestions = searchOverlay?.querySelector('[data-tapn-search-suggestions]');
    const searchResults = searchOverlay?.querySelector('[data-tapn-search-results]');
    const searchEmpty = searchOverlay?.querySelector('[data-tapn-search-empty]');
    const cartButton = header.querySelector('[data-tapn-cart-open]');
    const cartCount = header.querySelector('[data-tapn-cart-count]');
    const cartOverlay = document.querySelector('[data-tapn-cart-overlay]');
    const cartDrawer = cartOverlay?.querySelector('[data-tapn-cart-drawer]');
    const cartCloseButton = cartOverlay?.querySelector('[data-tapn-cart-close]');
    const cartItems = cartOverlay?.querySelector('[data-tapn-cart-items]');
    // ─── Offre « guide offert avec une carte Google » ───────────────────────
    // Dormante tant que le produit du guide est en brouillon : header.liquid
    // n'écrit alors aucun de ces attributs, l'identifiant vaut 0 et tout le
    // code de l'offre (mention « Offert », synchronisation) reste inactif.
    const guideVariantId = Number(cartOverlay?.dataset.guideVariantId) || 0;
    const guideEligibleProductIds = new Set(
      (cartOverlay?.dataset.guideEligibleProductIds || '').split(',').map(Number).filter(Boolean),
    );
    const guideBadgeUrl = cartOverlay?.dataset.guideBadgeUrl || '';
    // Ligne du guide ajoutée par le thème (propriété cachée `_offre`) ET gratuite :
    // le client ne peut ni la retirer ni changer sa quantité. Elle repart toute seule
    // avec la carte (syncGuideOffer). Si la remise ne s'applique plus, la ligne
    // redevient modifiable : le client ne reste jamais bloqué avec une ligne payante.
    const isLockedGuideLine = (item) => Boolean(guideVariantId)
      && item.variant_id === guideVariantId
      && item.properties?._offre === 'guide'
      && item.final_line_price === 0;
    const cartTotal = cartOverlay?.querySelector('[data-tapn-cart-total]');
    const cartCheckout = cartOverlay?.querySelector('[data-tapn-cart-checkout]');
    const cartDiscountInput = cartOverlay?.querySelector('[data-tapn-cart-discount-input]');
    const cartDiscountButton = cartOverlay?.querySelector('[data-tapn-cart-discount-apply]');
    const cartDiscountStatus = cartOverlay?.querySelector('[data-tapn-cart-discount-status]');
    const cartShippingBar = cartOverlay?.querySelector('[data-tapn-cart-shipping-bar]');
    const cartShippingLabel = cartShippingBar?.querySelector('[data-tapn-cart-shipping-bar-label]');
    const cartShippingFill = cartShippingBar?.querySelector('[data-tapn-cart-shipping-bar-fill]');
    const cartRecommendationsList = cartOverlay?.querySelector('[data-cart-recommendations-list]');
    const cartRecommendationsPrev = cartOverlay?.querySelector('[data-cart-recommendations-prev]');
    const cartRecommendationsNext = cartOverlay?.querySelector('[data-cart-recommendations-next]');
    const shippingThreshold = cartShippingBar ? Math.round(parseFloat(cartShippingBar.dataset.threshold) * 100) : 0;
    const currency = window.Shopify?.currency?.active || 'EUR';
    const money = new Intl.NumberFormat('fr-FR', { style: 'currency', currency, minimumFractionDigits: 2 });
    let lastY = window.scrollY;
    let searchTimer = 0;
    let searchRequest;
    let searchCloseTimer;
    let previousOverflow = '';
    let cartTrigger;
    let mobileMenuCloseTimer;
    let cartCloseTimer;

    const updateHeader = () => {
      const currentY = window.scrollY;
      header.classList.toggle('is-scrolled', currentY > 40);
      const menuOpen = Boolean(mobileMenu?.open);
      if (!menuOpen && currentY > lastY + 6 && currentY > 80) header.classList.add('is-hidden');
      if (menuOpen || currentY < lastY - 6 || currentY <= 80) header.classList.remove('is-hidden');
      lastY = currentY;
    };

    const setMobileMenuInert = (isOpen) => {
      document.querySelectorAll('main, footer').forEach((target) => {
        if (isOpen && !target.hasAttribute('inert')) {
          target.setAttribute('inert', '');
          target.dataset.tapnMobileMenuInert = 'true';
        } else if (!isOpen && target.dataset.tapnMobileMenuInert === 'true') {
          target.removeAttribute('inert');
          delete target.dataset.tapnMobileMenuInert;
        }
      });
    };

    const syncMobileSubmenuState = () => {
      mobileSubmenus.forEach((submenu) => {
        submenu.querySelector('.tapn-header__mobile-menu-link')?.setAttribute('aria-expanded', String(submenu.classList.contains('is-open')));
      });
    };

    // Accordéons "Cartes"/"Cartes de visite" : état piloté par classe + `hidden`,
    // pas par <details>/`open` (cf. commentaire markup dans header.liquid).
    const setSubmenuOpen = (submenu, isOpen) => {
      const trigger = submenu.querySelector('.tapn-header__mobile-menu-link');
      const content = submenu.querySelector('.tapn-header__mobile-submenu-content');
      submenu.classList.toggle('is-open', isOpen);
      trigger?.setAttribute('aria-expanded', String(isOpen));
      if (content) content.hidden = !isOpen;
    };

    const syncDropdownMenuState = ({ item, button }) => {
      if (!item || !button) return;
      const isOpen = item.classList.contains('is-open')
        || (!item.classList.contains('is-manually-closed')
          && (item.matches(':hover') || item.matches(':focus-within')));
      button.setAttribute('aria-expanded', String(isOpen));
    };

    const closeDropdownMenu = ({ item, button }, returnFocus = false, suppressHover = false) => {
      if (!item || !button) return;
      const wasOpen = item.classList.contains('is-open')
        || item.classList.contains('is-manually-closed')
        || item.matches(':hover')
        || item.matches(':focus-within');
      item.classList.remove('is-open');
      item.classList.toggle('is-manually-closed', suppressHover);
      button.setAttribute('aria-expanded', 'false');
      if (returnFocus && wasOpen) button.focus();
    };

    const toggleDropdownMenu = ({ item, button }) => {
      if (!item || !button) return;
      const isOpen = item.classList.contains('is-open');
      item.classList.toggle('is-open', !isOpen);
      item.classList.toggle('is-manually-closed', isOpen);
      button.setAttribute('aria-expanded', String(!isOpen));
    };

    const loadMobileMenuImages = () => {
      mobileMenu?.querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = 'eager'; });
    };

    const syncMobileMenuState = () => {
      const isOpen = Boolean(mobileMenu?.open) && !mobileMenu.classList.contains('is-closing');
      if (isOpen) loadMobileMenuImages();
      mobileMenuSummary?.setAttribute('aria-expanded', String(isOpen));
      mobileMenuSummary?.setAttribute('aria-label', isOpen ? 'Fermer le menu' : 'Ouvrir le menu');
      syncMobileSubmenuState();
      setMobileMenuInert(isOpen);
      updateHeader();
    };

    const closeMobileMenu = (returnFocus = false) => {
      if (!mobileMenu?.open && !mobileMenu?.classList.contains('is-closing')) return;
      window.clearTimeout(mobileMenuCloseTimer);
      mobileSubmenus.forEach((submenu) => setSubmenuOpen(submenu, false));
      mobileMenu.removeAttribute('open');
      mobileMenu.classList.remove('is-closing');
      syncMobileMenuState();
      if (returnFocus) mobileMenuSummary?.focus();
    };

    const getMobileMenuFocusable = () => {
      if (!mobileMenu) return [];
      return [...mobileMenu.querySelectorAll('summary, a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])')]
        .filter((element) => element.getClientRects().length > 0);
    };

    const setSearchState = (state) => {
      if (!searchSuggestions || !searchResults || !searchEmpty) return;
      searchSuggestions.hidden = state !== 'suggestions';
      searchResults.hidden = state !== 'results';
      searchEmpty.hidden = state !== 'empty';
    };

    const closeSearch = () => {
      if (!searchOverlay || searchOverlay.hidden) return;
      window.clearTimeout(searchCloseTimer);
      searchOverlay.hidden = true;
      searchButton?.setAttribute('aria-expanded', 'false');
      searchRequest?.abort();
      window.clearTimeout(searchTimer);
      document.body.style.overflow = previousOverflow;

      if (reducedMotion.matches) {
        searchOverlay.classList.remove('is-open', 'is-closing');
        return;
      }

      searchOverlay.hidden = false;
      searchOverlay.classList.remove('is-open');
      searchOverlay.classList.add('is-closing');
      searchCloseTimer = window.setTimeout(() => {
        searchOverlay.hidden = true;
        searchOverlay.classList.remove('is-open', 'is-closing');
      }, 440);
    };

    const openSearch = () => {
      if (!searchOverlay || !searchInput) return;
      window.clearTimeout(searchCloseTimer);
      closeCart();
      closeMobileMenu();
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      searchOverlay.hidden = false;
      searchOverlay.classList.remove('is-closing');
      searchButton?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        if (searchOverlay.hidden) return;
        searchOverlay.classList.add('is-open');
        searchInput.focus();
      });
    };

    const normalizeImageUrl = (value) => {
      const source = typeof value === 'string' ? value : value?.url;
      if (!source) return '';
      return source.startsWith('//') ? `https:${source}` : source;
    };

    const renderSearchResults = (products, query) => {
      if (!searchResults || !searchEmpty) return;
      searchResults.replaceChildren();
      if (products.length === 0) {
        searchEmpty.textContent = `Aucun résultat pour « ${query} ».`;
        setSearchState('empty');
        return;
      }

      products.forEach((product) => {
        const item = document.createElement('li');
        item.className = 'tapn-search-result';
        const frame = document.createElement('div');
        frame.className = 'tapn-search-result__frame';
        const mediaLink = document.createElement('a');
        mediaLink.href = product.url;
        mediaLink.tabIndex = -1;
        mediaLink.setAttribute('aria-hidden', 'true');
        mediaLink.addEventListener('click', closeSearch, listenerOptions);
        const media = document.createElement('span');
        media.className = 'tapn-search-result__media';
        const imageUrl = normalizeImageUrl(product.image || product.featured_image);
        if (imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = '';
          image.loading = 'lazy';
          media.append(image);
        }
        mediaLink.append(media);
        frame.append(mediaLink);
        const link = document.createElement('a');
        link.href = product.url;
        link.addEventListener('click', closeSearch, listenerOptions);
        const title = document.createElement('strong');
        title.textContent = product.title;
        link.append(title);
        const numericPrice = Number.parseFloat(product.price);
        if (Number.isFinite(numericPrice)) {
          const price = document.createElement('em');
          price.textContent = money.format(numericPrice);
          link.append(price);
        }
        const addButton = document.createElement('button');
        addButton.type = 'button';
        addButton.className = 'tapn-search-add';
        addButton.dataset.tapnSearchAdd = '';
        addButton.dataset.productUrl = product.url;
        if (product.available === false) addButton.disabled = true;
        addButton.setAttribute('aria-label', product.available === false ? `${product.title} est épuisé` : `Ajouter ${product.title} au panier`);
        addButton.innerHTML = `<span class="tapn-search-add__label" data-tapn-search-add-label>${product.available === false ? 'Épuisé' : 'Ajouter au panier'}</span><svg class="tapn-search-add__icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg><span class="tapn-search-add__spinner" aria-hidden="true"></span><svg class="tapn-search-add__check" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7"/></svg>`;
        frame.append(addButton);
        item.append(frame, link);
        searchResults.append(item);
      });
      setSearchState('results');
    };

    // ─── Ajout au panier depuis la recherche ──────────────────────────────
    // Les résultats de recherche prédictive n'exposent jamais de variante
    // (limite de l'API Shopify) : on la résout à la demande via `product.js`
    // au clic. Les blocs « Article en tendance » connaissent déjà la leur.
    const addSearchProductToCart = async (button) => {
      if (button.disabled) return;
      const label = button.querySelector('[data-tapn-search-add-label]');
      const original = label ? label.textContent : button.textContent;
      const originalAriaLabel = button.getAttribute('aria-label');
      button.disabled = true;
      button.classList.add('is-loading');
      if (label) label.textContent = 'Ajout…';
      button.setAttribute('aria-label', 'Ajout au panier en cours');
      let success = false;
      try {
        let variantId = button.dataset.variantId;
        if (!variantId && button.dataset.productUrl) {
          const path = new URL(button.dataset.productUrl, window.location.origin).pathname;
          const response = await fetch(`${path}.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('product fetch failed');
          const product = await response.json();
          variantId = (product.variants.find((variant) => variant.available) || product.variants[0])?.id;
        }
        if (!variantId) throw new Error('no variant');
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!response.ok) throw new Error('add failed');
        success = true;
      } catch {
        success = false;
      }
      if (label) label.textContent = success ? 'Ajouté' : 'Réessayer';
      button.classList.toggle('is-success', success);
      button.setAttribute('aria-label', success ? 'Produit ajouté au panier' : 'Réessayer l’ajout au panier');
      if (success) document.dispatchEvent(new CustomEvent('tapn:cart-updated'));
      window.setTimeout(() => {
        button.classList.remove('is-loading', 'is-success');
        button.disabled = false;
        if (label) label.textContent = original;
        if (originalAriaLabel) button.setAttribute('aria-label', originalAriaLabel);
      }, success ? 1200 : 1600);
    };

    // Une suggestion populaire ("Carte Google", "Carte TikTok"…) doit ne
    // proposer que les produits de cette gamme, jamais l'ensemble « Carte »
    // que Shopify associe par défaut (toutes nos fiches commencent par ce
    // mot). `strictKeyword` filtre le résultat de recherche natif après
    // coup sur le dernier mot de la suggestion ; une recherche tapée
    // librement n'est jamais filtrée. Signalé par Amine le 7 septembre 2026.
    const runSearch = async (rawQuery, strictKeyword = '') => {
      const query = rawQuery.trim();
      if (!query || !searchResults) {
        setSearchState('suggestions');
        return;
      }
      searchRequest?.abort();
      searchRequest = new AbortController();
      const base = searchResults.dataset.predictiveUrl || '/search/suggest';
      const url = new URL(base.endsWith('.json') ? base : `${base}.json`, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '20');
      url.searchParams.set('resources[options][unavailable_products]', 'hide');
      try {
        const response = await fetch(url, { signal: searchRequest.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const payload = await response.json();
        let products = payload.resources?.results?.products || [];
        if (strictKeyword) products = products.filter((product) => product.title?.toLowerCase().includes(strictKeyword));
        renderSearchResults(products, query);
      } catch (error) {
        if (error.name !== 'AbortError' && searchEmpty) {
          searchEmpty.textContent = 'La recherche est momentanément indisponible.';
          setSearchState('empty');
        }
      }
    };

    const updateCartCount = (count) => {
      if (!cartCount || !cartButton) return;
      cartCount.textContent = String(count);
      cartCount.hidden = count === 0;
      cartButton.setAttribute('aria-label', `Panier, ${count} article${count === 1 ? '' : 's'}`);
    };

    const closeCartSwipe = (swipe) => {
      const row = swipe.querySelector('.tapn-cart-item');
      swipe.classList.remove('is-swipe-open', 'is-swipe-dragging');
      row?.style.removeProperty('transform');
    };

    const bindCartItemSwipe = (swipe, row, remove) => {
      const actionWidth = 80;
      let pointerId;
      let startX = 0;
      let startY = 0;
      let startOffset = 0;
      let offset = 0;
      let direction = '';
      let suppressClick = false;

      const closeOtherSwipes = () => {
        cartItems?.querySelectorAll('.tapn-cart-item-swipe.is-swipe-open').forEach((other) => {
          if (other !== swipe) closeCartSwipe(other);
        });
      };

      const finishGesture = () => {
        if (pointerId === undefined) return;
        const shouldOpen = direction === 'horizontal' && offset <= -(actionWidth / 2);
        row.style.removeProperty('transform');
        swipe.classList.remove('is-swipe-dragging');
        if (shouldOpen) {
          closeOtherSwipes();
          swipe.classList.add('is-swipe-open');
        } else {
          swipe.classList.remove('is-swipe-open');
        }
        pointerId = undefined;
        direction = '';
      };

      swipe.addEventListener('pointerdown', (event) => {
        if (!mobileCartViewport.matches || (event.pointerType === 'mouse' && event.button !== 0)) return;
        if (event.target.closest('button, input, summary, select, textarea')) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        startOffset = swipe.classList.contains('is-swipe-open') ? -actionWidth : 0;
        offset = startOffset;
        direction = '';
        try { swipe.setPointerCapture(pointerId); } catch { /* Capture facultative. */ }
      }, listenerOptions);

      swipe.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        if (!direction) {
          if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
          direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
          if (direction === 'horizontal') swipe.classList.add('is-swipe-dragging');
        }
        if (direction !== 'horizontal') return;
        event.preventDefault();
        suppressClick = true;
        offset = Math.min(0, Math.max(-actionWidth, startOffset + deltaX));
        row.style.transform = `translateX(${offset}px)`;
      }, listenerOptions);

      swipe.addEventListener('pointerup', (event) => {
        if (event.pointerId !== pointerId) return;
        const draggedHorizontally = direction === 'horizontal';
        finishGesture();
        // Le clic synthétique éventuel du geste est bloqué, puis le prochain
        // toucher sur l'action révélée redevient immédiatement disponible.
        if (draggedHorizontally) window.setTimeout(() => { suppressClick = false; }, 0);
      }, listenerOptions);

      swipe.addEventListener('pointercancel', (event) => {
        if (event.pointerId !== pointerId) return;
        const draggedHorizontally = direction === 'horizontal';
        finishGesture();
        if (draggedHorizontally) window.setTimeout(() => { suppressClick = false; }, 0);
      }, listenerOptions);

      swipe.addEventListener('click', (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }, { capture: true, signal: lifecycle.signal });

      remove.addEventListener('focus', () => {
        if (!mobileCartViewport.matches) return;
        closeOtherSwipes();
        swipe.classList.add('is-swipe-open');
      }, listenerOptions);
      remove.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        closeCartSwipe(swipe);
        event.preventDefault();
      }, listenerOptions);
    };

    const createCartItem = (item) => {
      const swipe = document.createElement('div');
      swipe.className = 'tapn-cart-item-swipe';
      const row = document.createElement('article');
      row.className = 'tapn-cart-item';
      const media = document.createElement('span');
      media.className = 'tapn-cart-item__media';
      const imageUrl = normalizeImageUrl(item.image || item.featured_image);
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        media.append(image);
      }
      const copy = document.createElement('div');
      copy.className = 'tapn-cart-item__copy';
      const title = item.url ? document.createElement('a') : document.createElement('strong');
      title.className = 'tapn-cart-item__title';
      if (item.url) {
        title.href = item.url;
        title.addEventListener('click', closeCart, listenerOptions);
      }
      title.textContent = item.product_title || item.title;
      copy.append(title);
      const details = [];
      if (item.variant_title && !/^(default title|titre par défaut)$/i.test(item.variant_title.trim())) {
        const variantTitle = item.variant_title;
        const normalizedVariant = variantTitle.toLowerCase();
        let toneClass = 'neutral';
        if ((normalizedVariant.includes('bleu') || normalizedVariant.includes('blue')) && normalizedVariant.includes('blanc')) toneClass = 'blue-duo';
        else if ((normalizedVariant.includes('noir') || normalizedVariant.includes('black')) && normalizedVariant.includes('blanc')) toneClass = 'black-duo';
        else if (normalizedVariant.includes('noir') || normalizedVariant.includes('black')) toneClass = 'black';
        else if (normalizedVariant.includes('blanc') || normalizedVariant.includes('white')) toneClass = 'white';
        else if (normalizedVariant.includes('or') || normalizedVariant.includes('gold')) toneClass = 'gold';
        else if (normalizedVariant.includes('argent') || normalizedVariant.includes('silver')) toneClass = 'silver';
        // La variante est volontairement représentée par une seule pastille de
        // couleur. Son libellé reste accessible aux lecteurs d'écran.
        if (toneClass !== 'neutral') {
          const variant = document.createElement('span');
          variant.className = 'tapn-cart-item__variant';
          const variantSwatch = document.createElement('span');
          variantSwatch.className = `tapn-cart-item__variant-swatch tapn-cart-item__variant-swatch--${toneClass}`;
          variantSwatch.setAttribute('role', 'img');
          variantSwatch.setAttribute('aria-label', variantTitle);
          variant.append(variantSwatch);
          copy.append(variant);
        }
      }
      Object.entries(item.properties || {}).forEach(([key, value]) => {
        if (value && !key.startsWith('_')) details.push(`${key} : ${value}`);
      });
      details.slice(0, 2).forEach((detail) => {
        const line = document.createElement('small');
        line.textContent = detail;
        copy.append(line);
      });
      // Guide offert : la remise Shopify ramène la ligne à 0 €. La classe
      // conserve le traitement visuel et le verrouillage sans ajouter de
      // libellé redondant sous le titre.
      let guideBadge;
      if (guideVariantId && item.variant_id === guideVariantId && item.final_line_price === 0) {
        row.classList.add('tapn-cart-item--offered');
        if (guideBadgeUrl) {
          guideBadge = document.createElement('span');
          guideBadge.className = 'tapn-cart-item__guide-badge';
          guideBadge.setAttribute('aria-hidden', 'true');
          const badgeImage = document.createElement('img');
          badgeImage.src = guideBadgeUrl;
          badgeImage.alt = '';
          badgeImage.loading = 'lazy';
          guideBadge.append(badgeImage);
        }
      }
      const price = document.createElement('p');
      price.className = 'tapn-cart-item__price';
      const originalLinePrice = Number(item.original_line_price);
      const finalLinePrice = Number(item.final_line_price);
      if (isLockedGuideLine(item) && originalLinePrice > finalLinePrice) {
        const originalPrice = document.createElement('s');
        originalPrice.className = 'tapn-cart-item__price-original';
        originalPrice.textContent = money.format(originalLinePrice / 100);
        const currentPrice = document.createElement('span');
        currentPrice.className = 'tapn-cart-item__price-current';
        currentPrice.textContent = money.format(finalLinePrice / 100);
        price.append(currentPrice, originalPrice);
      } else {
        price.textContent = money.format(finalLinePrice / 100);
      }
      // Le prix va dans le bloc de texte, juste sous le titre (avant la pastille de
      // coloris et les détails) : ordre titre, prix, pastille, demande d'Amine du
      // 22 septembre 2026. Il ne vit donc plus dans la grille de la ligne.
      title.after(price);
      if (isLockedGuideLine(item)) {
        row.append(media, copy);
        if (guideBadge) row.append(guideBadge);
        swipe.append(row);
        return swipe;
      }
      const controls = document.createElement('div');
      controls.className = 'tapn-cart-item__controls';
      const quantity = document.createElement('div');
      quantity.className = 'tapn-cart-quantity';
      const decrease = document.createElement('button');
      decrease.type = 'button';
      decrease.textContent = '−';
      decrease.disabled = item.quantity <= 1;
      decrease.setAttribute('aria-label', `Réduire la quantité de ${item.product_title || item.title}`);
      decrease.addEventListener('click', () => changeCart(item.key, item.quantity - 1), listenerOptions);
      const amount = document.createElement('span');
      amount.textContent = String(item.quantity);
      const increase = document.createElement('button');
      increase.type = 'button';
      increase.textContent = '+';
      increase.setAttribute('aria-label', `Augmenter la quantité de ${item.product_title || item.title}`);
      increase.addEventListener('click', () => changeCart(item.key, item.quantity + 1), listenerOptions);
      quantity.append(decrease, amount, increase);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'tapn-cart-remove';
      remove.setAttribute('aria-label', `Retirer ${item.product_title || item.title} du panier`);
      remove.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';
      remove.addEventListener('click', () => changeCart(item.key, 0), listenerOptions);
      controls.append(quantity);
      row.append(media, copy, controls);
      if (guideBadge) row.append(guideBadge);
      swipe.append(row, remove);
      bindCartItemSwipe(swipe, row, remove);
      return swipe;
    };

    // ─── Synchronisation du guide offert ────────────────────────────────────
    // Ajoute le guide au panier quand une carte Google y est, le retire quand
    // elle repart. Le prix (0 €) est décidé par Shopify via la remise « Guide
    // offert » : ce code ne fait que placer la ligne dans le panier. La ligne
    // ajoutée porte la propriété cachée `_offre` : c'est elle qui la distingue
    // d'un guide acheté seul par le client, qui n'est jamais retiré.
    const postCart = (url, body) => fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    let guideSyncing = false;
    const syncGuideOffer = async (cart) => {
      if (!guideVariantId || guideSyncing) return cart;
      const hasEligibleCard = cart.items.some((item) => guideEligibleProductIds.has(item.product_id));
      const guideLine = cart.items.find((item) => item.variant_id === guideVariantId);

      let request;
      if (hasEligibleCard && !guideLine) {
        request = () => postCart('/cart/add.js', { items: [{ id: guideVariantId, quantity: 1, properties: { _offre: 'guide' } }] });
      } else if (!hasEligibleCard && guideLine?.properties?._offre === 'guide') {
        request = () => postCart('/cart/change.js', { id: guideLine.key, quantity: 0 });
      } else {
        return cart;
      }

      guideSyncing = true;
      try {
        const response = await request();
        if (!response.ok) return cart;
        const fresh = await fetch(cartOverlay?.dataset.cartJsonUrl || '/cart.js', { headers: { Accept: 'application/json' } });
        return fresh.ok ? await fresh.json() : cart;
      } catch {
        return cart;
      } finally {
        guideSyncing = false;
      }
    };

    // ─── Recommandations : masquer ce qui est déjà au panier ──────────────
    // Le bloc est du HTML statique rendu par Liquid ; seule la visibilité de
    // chaque carte change. La comparaison se fait variante par variante et non
    // par produit : tant qu'un coloris n'est pas au panier, la carte reste utile.
    const updateCartRecommendations = (cart) => {
      const cartVariantIds = new Set(cart.items.map((item) => String(item.variant_id)));
      cartOverlay?.querySelectorAll('[data-cart-recommendation]').forEach((card) => {
        const variantIds = (card.dataset.variantIds || '').split(',').filter(Boolean);
        card.hidden = variantIds.length > 0 && variantIds.every((id) => cartVariantIds.has(id));
      });
    };

    // ─── Choix du coloris dans une recommandation ─────────────────────────
    // La pastille cliquée devient la variante que le bouton « Ajouter » enverra,
    // et met à jour le visuel et le prix affichés pour que la carte ne montre
    // jamais un coloris différent de celui qui partira au panier.
    const selectRecommendationVariant = (swatch) => {
      const card = swatch.closest('[data-cart-recommendation]');
      if (!card) return;
      card.querySelectorAll('[data-cart-recommendation-swatch]').forEach((other) => {
        other.setAttribute('aria-pressed', String(other === swatch));
      });
      // Les deux liens (visuel et titre) suivent le coloris choisi : sans ça, la
      // pastille changerait l'aperçu mais mènerait toujours à la même variante.
      card.querySelectorAll('[data-product-url]').forEach((link) => {
        link.href = `${link.dataset.productUrl}?variant=${swatch.dataset.variantId}`;
      });
      const addButton = card.querySelector('[data-cart-recommendation-add]');
      if (addButton) {
        addButton.dataset.variantId = swatch.dataset.variantId;
        // Un coloris épuisé reste sélectionnable pour rester visible, mais le
        // bouton le dit au lieu de laisser partir un ajout qui échouerait.
        const available = swatch.dataset.variantAvailable !== 'false';
        addButton.disabled = !available;
        // Le libellé passe par le <span> interne : le bouton porte aussi le
        // spinner et la coche, écrire dans son textContent les effacerait.
        const label = addButton.querySelector('[data-cart-recommendation-add-label]');
        if (label) label.textContent = available ? 'Ajouter' : 'Épuisé';
      }
      const price = card.querySelector('[data-cart-recommendation-price]');
      if (price && swatch.dataset.variantPrice) price.textContent = swatch.dataset.variantPrice;
      const image = card.querySelector('[data-cart-recommendation-media] img');
      if (image && swatch.dataset.variantImage) image.src = swatch.dataset.variantImage;
    };

    // ─── Barre de progression vers la livraison offerte ────────────────────
    // Calculée sur `cart.total_price` (après remise), pas le sous-total : un
    // code promo qui repasse le panier sous le seuil doit rouvrir la barre,
    // sinon elle affiche « débloquée » alors que Shopify facture la livraison.
    // Les articles sans expédition (l'ebook) sont retirés du calcul : ils ne
    // se livrent pas, donc ne rapprochent pas du seuil. Sans aucun article à
    // expédier (ebook seul), la barre n'a plus de sens et se masque.
    const updateShippingBar = (cart) => {
      const digitalCents = cart.items
        .filter((item) => !item.requires_shipping)
        .reduce((sum, item) => sum + item.final_line_price, 0);
      const shippableCents = cart.total_price - digitalCents;
      if (cart.items.every((item) => !item.requires_shipping)) {
        if (cartShippingBar) cartShippingBar.hidden = true;
        return;
      }
      const remainingCents = Math.max(0, shippingThreshold - shippableCents);
      if (!cartShippingBar) return;
      cartShippingBar.hidden = false;
      const percent = Math.min(100, (shippableCents / shippingThreshold) * 100);
      if (cartShippingFill) cartShippingFill.style.width = `${percent}%`;
      if (cartShippingLabel) {
        cartShippingLabel.replaceChildren();
        if (remainingCents > 0) {
          const amount = document.createElement('strong');
          amount.textContent = money.format(remainingCents / 100);
          cartShippingLabel.append('Plus que ', amount, ' pour la livraison offerte');
        } else {
          cartShippingLabel.append('Livraison offerte débloquée');
        }
      }
    };

    const renderCart = (cart) => {
      if (!cartItems || !cartTotal || !cartCheckout) return;
      cartItems.replaceChildren();
      updateCartCount(cart.item_count);
      cartTotal.textContent = money.format(cart.total_price / 100);
      cartCheckout.disabled = cart.item_count === 0;
      cartDrawer?.classList.toggle('is-empty', cart.items.length === 0);
      updateCartRecommendations(cart);
      updateShippingBar(cart);
      if (cart.items.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'tapn-cart-empty';
        const title = document.createElement('strong');
        title.textContent = 'Votre panier est vide.';
        const text = document.createElement('span');
        text.textContent = 'Ajoutez une carte pour commencer.';
        empty.append(title, text);
        cartItems.append(empty);
        return;
      }
      cart.items.forEach((item) => cartItems.append(createCartItem(item)));
    };

    const fetchCart = async () => {
      const url = cartOverlay?.dataset.cartJsonUrl || '/cart.js';
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      const cart = await syncGuideOffer(await response.json());
      renderCart(cart);
      return cart;
    };

    const applyCartDiscount = () => {
      const code = cartDiscountInput?.value.trim();
      if (!code) {
        if (cartDiscountStatus) {
          cartDiscountStatus.textContent = 'Saisissez un code promo.';
          cartDiscountStatus.hidden = false;
        }
        cartDiscountInput?.focus();
        return;
      }

      if (cartDiscountButton) cartDiscountButton.disabled = true;
      if (cartDiscountStatus) {
        cartDiscountStatus.textContent = 'Application du code promo…';
        cartDiscountStatus.hidden = false;
      }
      const base = cartOverlay?.dataset.discountBase || '/discount/';
      const redirect = cartOverlay?.dataset.cartRedirect || '/cart';
      window.location.assign(`${base}${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`);
    };

    // ─── Ajout direct depuis les recommandations du panier ────────────────
    async function addRecommendation(button) {
      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled) return;
      button.disabled = true;
      button.classList.remove('is-success');
      button.classList.add('is-loading');
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);
        await fetchCart();
        // Coche affichée un instant pour confirmer l'ajout avant le retour au
        // repos ; le libellé « Ajouter » ne change jamais de texte.
        button.classList.remove('is-loading');
        button.classList.add('is-success');
        await new Promise((resolve) => setTimeout(resolve, 900));
      } catch {
        /* Le panier reste tel quel ; le bouton est remis en état ci-dessous. */
      } finally {
        // Toujours restaurer, y compris après un ajout réussi : la carte peut
        // rester à l'écran (autre coloris disponible) ou revenir après que le
        // client a vidé son panier, et son bouton doit alors être réutilisable.
        button.disabled = false;
        button.classList.remove('is-loading', 'is-success');
      }
    }

    async function changeCart(id, quantity) {
      const url = cartOverlay?.dataset.cartChangeUrl || '/cart/change.js';
      const endpoint = url.endsWith('.js') ? url : `${url}.js`;
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id, quantity }),
        });
        if (!response.ok) throw new Error(`Cart change failed: ${response.status}`);
        renderCart(await syncGuideOffer(await response.json()));
      } catch {
        await fetchCart().catch(() => {});
      }
    }

    function closeCart() {
      if (!cartOverlay || cartOverlay.hidden) return;
      window.clearTimeout(cartCloseTimer);
      cartButton?.setAttribute('aria-expanded', 'false');
      cartTrigger?.focus();
      cartTrigger = undefined;
      document.body.style.overflow = previousOverflow;
      if (reducedMotion.matches) {
        cartOverlay.hidden = true;
        cartOverlay.classList.remove('is-open', 'is-closing');
        return;
      }
      cartOverlay.classList.remove('is-open');
      cartOverlay.classList.add('is-closing');
      cartCloseTimer = window.setTimeout(() => {
        cartOverlay.hidden = true;
        cartOverlay.classList.remove('is-open', 'is-closing');
      }, 440);
    }

    const openCart = async () => {
      if (!cartOverlay) return;
      closeSearch();
      closeMobileMenu();
      cartTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : cartButton;
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      window.clearTimeout(cartCloseTimer);
      cartOverlay.hidden = false;
      cartOverlay.classList.remove('is-closing');
      cartButton?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        cartOverlay.classList.add('is-open');
        cartCloseButton?.focus();
      });
      await fetchCart().catch(() => {
        if (cartItems) cartItems.textContent = 'Le panier est momentanément indisponible.';
      });
    };

    // Rattrape les ajouts qui ne passent pas par le panier latéral (formulaire
    // produit classique, page panier après rechargement) : à chaque chargement de
    // page, le guide est aligné sur le contenu réel du panier. Sur la page panier,
    // qui est rendue côté serveur, un changement impose un rechargement.
    if (guideVariantId) {
      fetch(cartOverlay?.dataset.cartJsonUrl || '/cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then(async (cart) => {
          if (!cart) return;
          const synced = await syncGuideOffer(cart);
          if (synced === cart) return;
          renderCart(synced);
          if (document.querySelector('[data-tapn-cart-page]')) window.location.reload();
        })
        .catch(() => {});
    }

    syncMobileMenuState();
    dropdownMenus.forEach((menu) => {
      menu.button?.addEventListener('click', (event) => {
        event.preventDefault();
        toggleDropdownMenu(menu);
      }, listenerOptions);
      menu.item?.addEventListener('pointerenter', () => {
        if (!menu.item.classList.contains('is-open')) menu.item.classList.remove('is-manually-closed');
        syncDropdownMenuState(menu);
      }, listenerOptions);
      menu.item?.addEventListener('pointerleave', () => syncDropdownMenuState(menu), listenerOptions);
      menu.item?.addEventListener('focusin', () => syncDropdownMenuState(menu), listenerOptions);
      menu.item?.addEventListener('focusout', () => window.requestAnimationFrame(() => syncDropdownMenuState(menu)), listenerOptions);
    });
    document.addEventListener('pointerdown', (event) => {
      dropdownMenus.forEach((menu) => {
        if (!menu.item?.contains(event.target)) closeDropdownMenu(menu);
      });
    }, listenerOptions);
    window.addEventListener('scroll', updateHeader, { passive: true, signal: lifecycle.signal });
    // Même bug WebKit que les sous-menus (voir plus bas) : la bascule native
    // du <summary> ne s'ouvrait pas toujours au 1er tap. On pilote `open`
    // nous-mêmes dans les deux sens plutôt que de ne gérer que la fermeture.
    // Signalé de nouveau par Amine le 7 septembre 2026 (1er correctif du même
    // jour n'avait couvert que les sous-menus, pas l'icône elle-même).
    mobileMenuSummary?.addEventListener('click', (event) => {
      event.preventDefault();
      if (mobileMenu?.open && !mobileMenu.classList.contains('is-closing')) {
        closeMobileMenu();
      } else if (mobileMenu && !mobileMenu.open) {
        mobileMenu.open = true;
      }
    }, listenerOptions);
    mobileMenu?.addEventListener('toggle', syncMobileMenuState, listenerOptions);
    mobileSubmenus.forEach((submenu) => {
      submenu.querySelector('.tapn-header__mobile-menu-link')?.addEventListener('click', () => {
        const willOpen = !submenu.classList.contains('is-open');
        mobileSubmenus.forEach((other) => setSubmenuOpen(other, other === submenu && willOpen));
      }, listenerOptions);
    });
    mobileMenu?.addEventListener('keydown', (event) => {
      if (!mobileMenu.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMobileMenu(true);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = getMobileMenuFocusable();
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, listenerOptions);
    mobileCartViewport.addEventListener('change', (event) => { if (!event.matches) closeMobileMenu(); }, listenerOptions);
    mobileMenu?.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => closeMobileMenu(), listenerOptions));
    brand?.addEventListener('click', (event) => {
      if (window.location.pathname !== '/' || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }, listenerOptions);
    searchButton?.addEventListener('click', openSearch, listenerOptions);
    searchOverlay?.querySelectorAll('[data-tapn-search-close]').forEach((button) => button.addEventListener('click', closeSearch, listenerOptions));
    searchOverlay?.querySelectorAll('[data-tapn-search-trend-link]').forEach((link) => link.addEventListener('click', closeSearch, listenerOptions));
    searchOverlay?.addEventListener('click', (event) => {
      const addButton = event.target.closest('[data-tapn-search-add]');
      if (addButton) addSearchProductToCart(addButton);
    }, listenerOptions);
    // Mobile : le clic hors dialog ne ferme plus la recherche, seul le bouton
    // de fermeture (croix, ex-« Annuler ») le fait (Amine, 7 septembre 2026 —
    // la zone hors dialog est trop proche du contenu pour un clic accidentel
    // sans conséquence).
    // Desktop : comportement inchangé, cliquer hors du panneau ferme toujours.
    searchOverlay?.addEventListener('pointerdown', (event) => {
      if (mobileCartViewport.matches) return;
      if (!searchDialog?.contains(event.target)) closeSearch();
    }, listenerOptions);
    searchInput?.addEventListener('input', () => {
      searchClear.hidden = searchInput.value.length === 0;
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => runSearch(searchInput.value), 180);
    }, listenerOptions);
    searchClear?.addEventListener('click', () => {
      searchInput.value = '';
      searchClear.hidden = true;
      setSearchState('suggestions');
      searchInput.focus();
    }, listenerOptions);
    searchOverlay?.querySelectorAll('[data-tapn-search-suggestion]').forEach((button) => button.addEventListener('click', () => {
      const value = button.dataset.tapnSearchSuggestion;
      searchInput.value = value;
      searchClear.hidden = false;
      const strictKeyword = value.trim().split(/\s+/).pop().toLowerCase();
      runSearch(value, strictKeyword);
    }, listenerOptions));
    searchForm?.addEventListener('submit', () => closeSearch(), listenerOptions);
    cartButton?.addEventListener('click', openCart, listenerOptions);
    document.addEventListener('tapn:cart-updated', (event) => {
      if (event.detail?.cart) renderCart(event.detail.cart);
      else fetchCart().catch(() => {});
    }, listenerOptions);
    cartOverlay?.querySelectorAll('[data-tapn-cart-close]').forEach((button) => button.addEventListener('click', closeCart, listenerOptions));
    cartOverlay?.querySelectorAll('[data-cart-recommendation-add]').forEach((button) => button.addEventListener('click', () => addRecommendation(button), listenerOptions));
    cartOverlay?.querySelectorAll('[data-cart-recommendation-swatch]').forEach((swatch) => swatch.addEventListener('click', () => selectRecommendationVariant(swatch), listenerOptions));
    // Flèches desktop : défilent d'environ une carte à la fois (largeur du
    // conteneur visible, pas de la liste entière qui déborde).
    cartRecommendationsPrev?.addEventListener('click', () => {
      cartRecommendationsList?.scrollBy({ left: -cartRecommendationsList.clientWidth * 0.9, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }, listenerOptions);
    cartRecommendationsNext?.addEventListener('click', () => {
      cartRecommendationsList?.scrollBy({ left: cartRecommendationsList.clientWidth * 0.9, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }, listenerOptions);
    cartOverlay?.addEventListener('pointerdown', (event) => { if (!cartDrawer?.contains(event.target)) closeCart(); }, listenerOptions);
    cartCheckout?.addEventListener('click', () => {
      if (!cartCheckout.disabled) window.location.href = cartOverlay.dataset.checkoutUrl || '/checkout';
    }, listenerOptions);
    cartDiscountButton?.addEventListener('click', applyCartDiscount, listenerOptions);
    cartDiscountInput?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      applyCartDiscount();
    }, listenerOptions);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        dropdownMenus.forEach((menu) => closeDropdownMenu(menu, true, true));
        closeSearch();
        closeCart();
        return;
      }
      if (event.key !== 'Tab' || !cartDrawer || cartOverlay?.hidden) return;
      const focusable = [...cartDrawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), summary, select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!cartDrawer.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, listenerOptions);
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.contains(header)) lifecycle.abort();
    }, listenerOptions);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initTapnHeader());
  else initTapnHeader();
  document.addEventListener('shopify:section:load', (event) => initTapnHeader(event.target));
})();
